import { useCallback, useMemo } from 'react';

import { useLoyaltyServiceOffer } from 'features/offer/hooks/use-loyalty-service-offer';

import { LoyaltyEngineId } from 'state/loyalty/types';

import { applyOfferStoreRestrictions } from '../utils/apply-offer-store-restrictions';
import { markOfferUnavailableForRecovery } from '../utils/build-unavailable-offer-recovery-list';
import {
  UnavailableOfferReason,
  resolveUnavailableOfferReasonMessage,
} from '../utils/unavailable-offer-reason';

/**
 * Resolves the card content for an offer that offer detail already settled as
 * unavailable, so the feed can pin it above the store's available offers.
 *
 * Callers must gate this on the explicit `unavailableItemId` recovery marker,
 * so every offer reaching here is already known to be unavailable and is marked
 * as such even when the lookup returns no inline message.
 *
 * The store-scoped lookup resolves to null whenever the selected restaurant
 * cannot redeem the offer, which leaves the feed with nothing to pin. The
 * store-agnostic lookup recovers the offer's name, image, and terms while
 * availability stays evaluated against the selected store.
 *
 * That lookup also runs when the store-scoped one succeeds, because the service
 * only returns `storeRestrictionMode` and `availableStoreIds` when `storeId` is
 * omitted. Without it the card's restaurant selector has no restriction to
 * filter ineligible restaurants with.
 */
export function useUnavailableOfferRecovery(
  loyaltyEngineId: LoyaltyEngineId,
  reason?: UnavailableOfferReason
) {
  const skip = !loyaltyEngineId;

  const {
    offerListItem: storeScopedOffer,
    hasSettled: hasStoreScopedSettled,
    isIncentivesLoading: isStoreScopedLoading,
    queryError: storeScopedError,
    refreshOffer: refreshStoreScopedOffer,
  } = useLoyaltyServiceOffer({
    loyaltyEngineId,
    skip,
    includeStoreRestricted: true,
  });

  const {
    offerListItem: storeAgnosticOffer,
    hasSettled: hasStoreAgnosticSettled,
    isIncentivesLoading: isStoreAgnosticLoading,
    queryError: storeAgnosticError,
    refreshOffer: refreshStoreAgnosticOffer,
  } = useLoyaltyServiceOffer({
    loyaltyEngineId,
    skip,
    includeStoreRestricted: true,
    omitStoreId: true,
  });

  const recoveryError = storeScopedError ?? storeAgnosticError;
  const hasRecoverySettled =
    skip || (hasStoreScopedSettled && hasStoreAgnosticSettled && !recoveryError);

  const unavailableOffer = useMemo(() => {
    if (!hasRecoverySettled) {
      return undefined;
    }

    const recoveredOffer = markOfferUnavailableForRecovery(
      applyOfferStoreRestrictions(storeScopedOffer ?? storeAgnosticOffer, storeAgnosticOffer)
    );
    const reasonMessage = resolveUnavailableOfferReasonMessage(reason);

    return recoveredOffer && reasonMessage
      ? { ...recoveredOffer, inlineAlertMessage: reasonMessage }
      : recoveredOffer;
  }, [hasRecoverySettled, reason, storeAgnosticOffer, storeScopedOffer]);

  const retryRecovery = useCallback(async () => {
    await Promise.all([refreshStoreScopedOffer(), refreshStoreAgnosticOffer()]);
  }, [refreshStoreAgnosticOffer, refreshStoreScopedOffer]);

  return {
    unavailableOffer,
    isRecovering:
      !skip &&
      !recoveryError &&
      (!hasRecoverySettled || isStoreScopedLoading || isStoreAgnosticLoading),
    recoveryError,
    retryRecovery,
  };
}
